import React, { Suspense, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCart } from "../cart";
import {
  currency,
  fetchProduct,
  REACT_APP_GOLDAPI_KEY,
  pickVariant,
  PLACEHOLDER_RING_WEIGHTS_G,
  placeholderImg,
  uniqueFromVariants,
} from "../shared";
import { trackAddToCart, trackViewItem } from "../analytics";
import NotFound from "./NotFound";
import { Canvas } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import * as THREE from "three";
import RingScene from "./RingScene";

class ThreeDErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch() {}

  render() {
    if (this.state.error) {
      return this.props.fallback(this.state.error);
    }
    return this.props.children;
  }
}

/** Product page — loads full product by parent SKU */
function ProductDetailPage() {
  const { sku } = useParams();
  const nav = useNavigate();
  const { addToCart } = useCart();

  const enable3DViewer =
    String(process.env.REACT_APP_ENABLE_3D_VIEWER || "true").toLowerCase() !==
    "false";

  const [show3D, setShow3D] = useState(false);
  const [threeDRetryKey, setThreeDRetryKey] = useState(0);
  const [threeDCheckedUrl, setThreeDCheckedUrl] = useState("");
  const [threeDCheckStatus, setThreeDCheckStatus] = useState("idle");
  const [threeDCheckError, setThreeDCheckError] = useState("");

  const SHOW_COST_FIELDS = false;

  const GOLDAPI_CACHE_ENABLED =
    String(process.env.REACT_APP_GOLDAPI_CACHE || "").toLowerCase() === "true";
  const GOLDAPI_CACHE_TTL_MS = 30 * 60 * 1000;

  const STONE_SETTING_PER_STONE = 1;
  const POLISHING_PER_GRAM = 20;
  const LAB_DIAMOND_MULTIPLIER = 0.25;
  const MARKUP_MULTIPLIER = 2;

  const INSURANCE_COST_PER_RING_PRICE = 90 / 3500;

  const MM_TO_CT_TABLE = [
    { mm: 1.0, ct: 0.005 },
    { mm: 1.1, ct: 0.006 },
    { mm: 1.2, ct: 0.008 },
    { mm: 1.3, ct: 0.01 },
    { mm: 1.4, ct: 0.012 },
    { mm: 1.5, ct: 0.015 },
    { mm: 1.6, ct: 0.018 },
    { mm: 1.7, ct: 0.02 },
    { mm: 1.8, ct: 0.025 },
    { mm: 1.9, ct: 0.03 },
    { mm: 2.0, ct: 0.035 },
    { mm: 2.1, ct: 0.04 },
    { mm: 2.2, ct: 0.045 },
    { mm: 2.3, ct: 0.05 },
    { mm: 2.4, ct: 0.06 },
    { mm: 2.5, ct: 0.07 },
    { mm: 2.7, ct: 0.09 },
    { mm: 3.0, ct: 0.1 },
    { mm: 3.2, ct: 0.13 },
    { mm: 3.5, ct: 0.17 },
    { mm: 3.8, ct: 0.21 },
    { mm: 4.0, ct: 0.25 },
    { mm: 4.3, ct: 0.3 },
    { mm: 4.5, ct: 0.36 },
    { mm: 4.8, ct: 0.45 },
    { mm: 5.0, ct: 0.5 },
    { mm: 5.2, ct: 0.6 },
    { mm: 5.5, ct: 0.7 },
    { mm: 5.8, ct: 0.8 },
    { mm: 6.0, ct: 0.9 },
    { mm: 6.3, ct: 1.0 },
    { mm: 6.5, ct: 1.05 },
    { mm: 6.8, ct: 1.2 },
    { mm: 7.0, ct: 1.25 },
    { mm: 7.5, ct: 1.5 },
    { mm: 8.0, ct: 2.0 },
    { mm: 8.5, ct: 2.25 },
    { mm: 9.0, ct: 2.75 },
    { mm: 9.5, ct: 3.0 },
    { mm: 10.0, ct: 3.5 },
    { mm: 10.5, ct: 4.0 },
    { mm: 11.0, ct: 5.0 },
  ];

  const NATURAL_PRICE_PER_CARAT_TABLE_MELEE = [
    { maxCtEach: 0.01, pricePerCt: 560 },
    { maxCtEach: 0.03, pricePerCt: 700 },
    { maxCtEach: 0.05, pricePerCt: 840 },
    { maxCtEach: 0.1, pricePerCt: 1030 },
    { maxCtEach: Infinity, pricePerCt: 1400 },
  ];

  // Solitaire natural pricing: calibrated so 0.50ct costs $650 (=$1300/ct).
  const NATURAL_PRICE_PER_CARAT_TABLE_SOLITAIRE = [
    { maxCtEach: 0.5, pricePerCt: 1300 },
    { maxCtEach: 1, pricePerCt: 1820 },
    { maxCtEach: 2, pricePerCt: 2430 },
    { maxCtEach: Infinity, pricePerCt: 3030 },
  ];

  const DWT_PER_GRAM = 0.643014931;
  const gramsToDwt = (grams) => grams * DWT_PER_GRAM;

  const METAL_CONFIG = {
    gold: {
      troyOzToDwt: 20,
      karats: {
        "10K": 10 / 24,
        "14K": 14 / 24,
        "18K": 18 / 24,
        "22K": 22 / 24,
        "24K": 1,
      },
      colors: {
        yellow: { multiplier: 1.2 },
        white: { multiplier: 1.22 },
        rose: { multiplier: 1.22 },
        // Default 18K rose/pink gold multiplier to 1.22 per Carrera invoice.
        // Multiplier represents alloy cost, refining loss, casting-house markup, and market buffer.
        pink: { multiplier: 1.22 },
      },
    },
    platinum: {
      troyOzToDwt: 20,
      purity: 0.95,
      // Multiplier represents alloy cost, refining loss, casting-house markup, and market buffer.
      multiplier: 1.15,
    },
  };

  const CostFields = ({
    goldErr,
    usingComputedPricing,
    metalCost,
    isPlatinum,
    selKarat,
    metalWeightGrams,
    castingDwt,
    castingCost,
    meleeStoneCost,
    solitaireStoneCost,
    laborCost,
    insuranceCost,
    totalCost,
    sellPrice,
    finalPrice,
  }) => (
    <>
      <div className="jh-field" style={{ marginTop: 10 }}>
        <label className="jh-label">Using computedPricing</label>
        <input
          className="jh-input"
          value={usingComputedPricing ? "Yes" : "No"}
          readOnly
        />
      </div>

      {goldErr ? (
        <div className="jh-sub" style={{ color: "#b91c1c" }}>
          Gold price unavailable
        </div>
      ) : metalCost != null ? (
        <div className="jh-sub">
          {isPlatinum ? "Platinum" : "Gold"} metal cost ({selKarat || ""},{" "}
          {metalWeightGrams}g): {currency(metalCost)}
        </div>
      ) : null}

      <div className="jh-field" style={{ marginTop: 10 }}>
        <label className="jh-label">
          Casting Cost ({castingDwt != null ? castingDwt.toFixed(2) : "0.00"}{" "}
          dwt)
        </label>
        <input className="jh-input" value={currency(castingCost)} readOnly />
      </div>

      <div className="jh-field" style={{ marginTop: 10 }}>
        <label className="jh-label">Melee Stone Cost</label>
        <input className="jh-input" value={currency(meleeStoneCost)} readOnly />
      </div>

      <div className="jh-field" style={{ marginTop: 10 }}>
        <label className="jh-label">Solitaire Stone Cost</label>
        <input
          className="jh-input"
          value={currency(solitaireStoneCost)}
          readOnly
        />
      </div>

      <div className="jh-field" style={{ marginTop: 10 }}>
        <label className="jh-label">
          Labor Cost (polishing + stone setting)
        </label>
        <input className="jh-input" value={currency(laborCost)} readOnly />
      </div>

      <div className="jh-field" style={{ marginTop: 10 }}>
        <label className="jh-label">Total Production Cost</label>
        <input className="jh-input" value={currency(totalCost)} readOnly />
      </div>

      <div className="jh-field" style={{ marginTop: 10 }}>
        <label className="jh-label">
          Sell Price ({MARKUP_MULTIPLIER}x markup)
        </label>
        <input className="jh-input" value={currency(sellPrice)} readOnly />
      </div>

      <div className="jh-field" style={{ marginTop: 10 }}>
        <label className="jh-label">Insurance Cost</label>
        <input className="jh-input" value={currency(insuranceCost)} readOnly />
      </div>

      <div className="jh-field" style={{ marginTop: 10 }}>
        <label className="jh-label">Final Price</label>
        <input className="jh-input" value={currency(finalPrice)} readOnly />
      </div>
    </>
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [product, setProduct] = useState(null);

  // option selections
  const [active, setActive] = useState(0);
  const [selSize, setSelSize] = useState(null);
  const [selMetal, setSelMetal] = useState(null);
  const [selKarat, setSelKarat] = useState(null);
  const [selStoneType, setSelStoneType] = useState("Natural diamond");
  const [qty, setQty] = useState(1);
  const [currentImages, setCurrentImages] = useState([]);

  const product3DFile = product?.["3Dfile"] || product?.["3dfile"] || "";
  const modelUrl = useMemo(() => {
    const raw = String(product3DFile || "").trim();
    if (!raw) return "";
    if (/^(https?:)?\/\//i.test(raw)) return raw;
    if (raw.startsWith("/")) return raw;
    return `/models/${encodeURIComponent(raw)}`;
  }, [product3DFile]);

  const canShow3DOption = enable3DViewer && Boolean(modelUrl);

  const open3D = () => {
    if (!canShow3DOption) return;
    setThreeDRetryKey((k) => k + 1);
    setShow3D(true);
    setThreeDCheckStatus("checking");
    setThreeDCheckError("");
    setThreeDCheckedUrl(modelUrl);
  };

  useEffect(() => {
    if (show3D && !enable3DViewer) setShow3D(false);
  }, [enable3DViewer, show3D]);

  useEffect(() => {
    if (!show3D) return;
    if (!threeDCheckedUrl) return;
    if (threeDCheckStatus !== "checking") return;

    let mounted = true;
    const ctrl = new AbortController();

    fetch(threeDCheckedUrl, {
      method: "GET",
      signal: ctrl.signal,
      headers: { Range: "bytes=0-3" },
    })
      .then(async (res) => {
        if (!mounted) return;
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const buf = await res.arrayBuffer();
        const bytes = new Uint8Array(buf);
        const isGlb =
          bytes.length >= 4 &&
          bytes[0] === 0x67 &&
          bytes[1] === 0x6c &&
          bytes[2] === 0x54 &&
          bytes[3] === 0x46;

        if (!isGlb) {
          throw new Error("3D file was not found");
        }

        setThreeDCheckStatus("ok");
      })
      .catch((e) => {
        if (!mounted) return;
        setThreeDCheckStatus("error");
        setThreeDCheckError(String(e?.message || e));
      });

    return () => {
      mounted = false;
      ctrl.abort();
    };
  }, [show3D, threeDCheckedUrl, threeDCheckStatus]);

  const isPlatinum = /platinum/i.test(String(selMetal || ""));
  const platinumPurities = ["Pt950"];

  const [goldData, setGoldData] = useState(null);
  const [goldErr, setGoldErr] = useState("");

  // Update current images when metal selection or product changes
  useEffect(() => {
    if (!product) return;

    // Use selected metal or fall back to default from product
    const metalType = selMetal || product.defaultOptions?.metal_type;
    const metalImages = metalType ? product.metalImages?.[metalType] : [];

    if (metalImages?.length) {
      const newImages = metalImages.map((src, i) => ({
        src,
        alt: `${product.title} ${metalType} ${i + 1}`,
      }));
      setCurrentImages(newImages);
      setActive(0); // Reset active image when metal changes
    } else if (product.images?.length) {
      setCurrentImages(product.images);
      setActive(0);
    } else {
      setCurrentImages([{ src: placeholderImg, alt: product.title }]);
      setActive(0);
    }
  }, [product, selMetal]);

  useEffect(() => {
    if (!currentImages?.length) return;

    for (const im of currentImages) {
      if (!im?.src) continue;
      const img = new Image();
      img.src = im.src;
    }
  }, [currentImages]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    fetchProduct(sku)
      .then((doc) => {
        if (!mounted) return;
        const images = (doc.images || []).map((u, i) => ({
          src: u,
          alt: `${doc.title} ${i + 1}`,
        }));
        const sizes =
          doc.optionSchema?.ring_size ||
          uniqueFromVariants(doc.variants, "ring_size");
        const metals =
          doc.optionSchema?.metal_type ||
          uniqueFromVariants(doc.variants, "metal_type");
        const karats =
          doc.optionSchema?.karat ||
          doc.optionSchema?.gold_karat ||
          uniqueFromVariants(doc.variants, "karat") ||
          uniqueFromVariants(doc.variants, "gold_karat");
        const stoneTypes =
          doc.optionSchema?.stone_type ||
          uniqueFromVariants(doc.variants, "stone_type");
        // Get default options from product or use first available
        const defaultOptions = {
          metal_type: doc.defaultOptions?.metal_type || metals?.[0],
          ring_size: doc.defaultOptions?.ring_size || sizes?.[0],
          karat: doc.defaultOptions?.karat || karats?.[0],
          stone_type:
            doc.defaultOptions?.stone_type ||
            stoneTypes?.[0] ||
            "Natural diamond",
        };
        const normalized = {
          ...doc,
          images,
          sizes,
          metals,
          karats,
          stoneTypes,
          // Ensure metalImages exists and is an object
          metalImages: doc.metalImages || {},
          // Ensure defaultOptions has all required fields
          defaultOptions: {
            metal_type: defaultOptions.metal_type || null,
            ring_size: defaultOptions.ring_size || null,
            karat: defaultOptions.karat || null,
            ...(doc.defaultOptions || {}),
          },
        };

        setProduct(normalized);
        setActive(0);
        // Set selected options based on defaults or first available
        setSelSize(defaultOptions.ring_size || sizes?.[0] || null);
        setSelMetal(defaultOptions.metal_type || metals?.[0] || null);
        setSelKarat(defaultOptions.karat || karats?.[0] || null);
        setSelStoneType(defaultOptions.stone_type || "Natural diamond");
        setQty(1);
      })
      .catch((e) => {
        if (mounted) setError(String(e.message || e));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [sku]);

  useEffect(() => {
    if (!isPlatinum) return;
    if (selKarat && platinumPurities.includes(String(selKarat))) return;
    setSelKarat(platinumPurities[0]);
  }, [isPlatinum, selKarat]);

  useEffect(() => {
    if (isPlatinum) return;
    if (!selKarat || !platinumPurities.includes(String(selKarat))) return;
    const next = product?.defaultOptions?.karat || product?.karats?.[0] || null;
    setSelKarat(next);
  }, [isPlatinum, selKarat, product]);

  useEffect(() => {
    let mounted = true;
    console.log("get price");

    if (!REACT_APP_GOLDAPI_KEY) {
      setGoldData(null);
      setGoldErr("");
      return () => {
        mounted = false;
      };
    }

    console.log("get price2", REACT_APP_GOLDAPI_KEY);

    const symbol = isPlatinum ? "XPT" : "XAU";

    const cacheKey = `jh.goldapi.${symbol}.usd`;

    if (GOLDAPI_CACHE_ENABLED) {
      try {
        const raw = localStorage.getItem(cacheKey);
        if (raw) {
          const cached = JSON.parse(raw);
          const ts = typeof cached?.ts === "number" ? cached.ts : 0;
          if (cached?.data && Date.now() - ts < GOLDAPI_CACHE_TTL_MS) {
            console.log("GoldAPI cache hit", {
              symbol,
              ageMs: Date.now() - ts,
            });
            setGoldErr("");
            setGoldData(cached.data);
            return () => {
              mounted = false;
            };
          }
        }
      } catch {
        // ignore cache parse errors
      }
    }

    setGoldErr("");
    fetch(`https://www.goldapi.io/api/${symbol}/USD`, {
      method: "GET",
      headers: {
        "x-access-token": REACT_APP_GOLDAPI_KEY,
        "Content-Type": "application/json",
      },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`GoldAPI HTTP ${r.status}`);
        return r.json();
      })
      .then((json) => {
        if (!mounted) return;
        setGoldData(json);

        if (GOLDAPI_CACHE_ENABLED) {
          try {
            localStorage.setItem(
              cacheKey,
              JSON.stringify({ ts: Date.now(), data: json }),
            );
          } catch {
            // ignore cache write errors
          }
        }
      })
      .catch((e) => {
        if (!mounted) return;
        setGoldData(null);
        setGoldErr(String(e.message || e));
      });

    return () => {
      mounted = false;
    };
  }, [isPlatinum]);

  useEffect(() => {
    const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";
    if (!GA4_ID) return;
    if (!product?.sku || !product?.title) return;

    const variant = pickVariant(product, {
      ring_size: selSize,
      metal_type: selMetal,
      karat: selKarat,
      stone_type: selStoneType,
    });
    const unitPrice =
      variant?.salePrice ??
      variant?.price ??
      product.displayPrice ??
      product.salePrice ??
      product.price;
    const insuranceRate = INSURANCE_COST_PER_RING_PRICE;
    const insuranceCost =
      Number.isFinite(unitPrice) && Number.isFinite(insuranceRate)
        ? Number(unitPrice) * Number(insuranceRate)
        : 0;
    const finalPrice = (Number(unitPrice) || 0) + (Number(insuranceCost) || 0);
    const price =
      Number.isFinite(finalPrice) && finalPrice > 0 ? finalPrice : unitPrice;
    if (!Number.isFinite(price) || price <= 0) return;

    trackViewItem({
      measurementId: GA4_ID,
      sku: product.sku,
      name: product.title,
      price,
    });
  }, [
    INSURANCE_COST_PER_RING_PRICE,
    product,
    product?._id,
    selSize,
    selMetal,
    selKarat,
    selStoneType,
  ]);

  if (loading)
    return (
      <section className="jh-container jh-section">
        <div className="jh-sub">Loading...</div>
      </section>
    );
  if (error)
    return (
      <section className="jh-container jh-section">
        <div className="jh-sub" style={{ color: "#b91c1c" }}>
          Error: {error}
        </div>
      </section>
    );
  if (!product) return <NotFound message="Product not found" />;

  const variant = pickVariant(product, {
    ring_size: selSize,
    metal_type: selMetal,
    karat: selKarat,
    stone_type: selStoneType,
  });
  const unitPrice =
    variant?.salePrice ??
    variant?.price ??
    product.displayPrice ??
    product.salePrice ??
    product.price;

  const primaryImage =
    currentImages[active]?.src || currentImages[0]?.src || placeholderImg;

  const metalWeightGramsRaw =
    variant?.metalWeight ??
    product?.metalWeight ??
    PLACEHOLDER_RING_WEIGHTS_G[product?.sku] ??
    PLACEHOLDER_RING_WEIGHTS_G[sku] ??
    PLACEHOLDER_RING_WEIGHTS_G.default;
  const metalWeightGrams =
    metalWeightGramsRaw != null && metalWeightGramsRaw !== ""
      ? Number(metalWeightGramsRaw)
      : null;

  const stoneTotalRaw = variant?.stoneTotal ?? product?.stoneTotal;
  const stoneTotal =
    stoneTotalRaw != null && stoneTotalRaw !== ""
      ? Number(stoneTotalRaw)
      : null;

  const stoneSizesMm = variant?.stoneSizes ?? product?.stoneSizes;

  const solitaireCaratsRaw =
    variant?.solitaireCarats ?? product?.solitaireCarats;
  const solitaireCarats =
    solitaireCaratsRaw != null && solitaireCaratsRaw !== ""
      ? Number(solitaireCaratsRaw)
      : null;

  const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const spotPricePerOz =
    goldData && typeof goldData.price === "number" ? goldData.price : null;

  const goldColor = (() => {
    const s = String(selMetal || "").toLowerCase();
    if (s.includes("white")) return "white";
    if (s.includes("rose")) return "rose";
    if (s.includes("pink")) return "pink";
    if (s.includes("yellow")) return "yellow";
    return "yellow";
  })();

  const goldKaratKey = (() => {
    const s = String(selKarat || "").toUpperCase();
    if (s.includes("10")) return "10K";
    if (s.includes("14")) return "14K";
    if (s.includes("18")) return "18K";
    if (s.includes("22")) return "22K";
    if (s.includes("24")) return "24K";
    return null;
  })();

  const castingDwt =
    metalWeightGrams != null ? safeNum(gramsToDwt(metalWeightGrams)) : null;
  const perDwtPrice = (() => {
    if (spotPricePerOz == null) return null;
    const perDwtSpot = spotPricePerOz / 20;
    if (!isPlatinum) {
      const karatPurity = goldKaratKey
        ? METAL_CONFIG.gold.karats[goldKaratKey]
        : null;
      const colorMultiplier =
        METAL_CONFIG.gold.colors[goldColor]?.multiplier ??
        METAL_CONFIG.gold.colors.yellow.multiplier;
      if (karatPurity == null) return null;
      return perDwtSpot * karatPurity * colorMultiplier;
    }
    return (
      perDwtSpot *
      METAL_CONFIG.platinum.purity *
      METAL_CONFIG.platinum.multiplier
    );
  })();

  const castingMetalCost =
    castingDwt != null && perDwtPrice != null ? castingDwt * perDwtPrice : null;

  const karatDigits = String(selKarat || "")
    .toLowerCase()
    .replace(/[^0-9]/g, "");

  const goldGramKey = karatDigits ? `price_gram_${karatDigits}k` : "";
  const goldPricePerGram =
    goldData && goldGramKey && typeof goldData[goldGramKey] === "number"
      ? goldData[goldGramKey]
      : null;

  const platinumPricePerTroyOz =
    goldData && typeof goldData.price === "number" ? goldData.price : null;
  const platinumPricePerGram =
    platinumPricePerTroyOz != null
      ? platinumPricePerTroyOz / 31.1034768
      : goldData && typeof goldData.price_gram_24k === "number"
        ? goldData.price_gram_24k
        : null;
  const platinumPurityFactor = 0.95;

  const metalPricePerGram = isPlatinum
    ? platinumPricePerGram
    : goldPricePerGram;
  const metalCostFactor = isPlatinum ? platinumPurityFactor : 1;
  const metalCost =
    metalPricePerGram != null && metalWeightGrams != null
      ? metalPricePerGram * Number(metalWeightGrams) * metalCostFactor
      : null;

  const laborFee = 0;
  const shippingFee = 0;
  const castingSubtotal = castingMetalCost ?? metalCost ?? 0;
  const castingCost = castingSubtotal + laborFee + shippingFee;

  const polishingCost =
    metalWeightGrams != null && Number.isFinite(metalWeightGrams)
      ? metalWeightGrams * POLISHING_PER_GRAM
      : 0;
  const stoneSettingCost =
    stoneTotal != null && Number.isFinite(stoneTotal)
      ? stoneTotal * STONE_SETTING_PER_STONE
      : 0;

  const laborCost = polishingCost + stoneSettingCost;

  const sizeMm = safeNum(stoneSizesMm);
  const caratsEach = (() => {
    if (sizeMm == null) return null;
    let best = MM_TO_CT_TABLE[0];
    let bestDiff = Math.abs(sizeMm - best.mm);
    for (const row of MM_TO_CT_TABLE) {
      const diff = Math.abs(sizeMm - row.mm);
      if (diff < bestDiff) {
        best = row;
        bestDiff = diff;
      }
    }
    return best?.ct ?? null;
  })();

  const naturalPricePerCtFor = (table, ctEach) => {
    if (ctEach == null) return null;
    const row = table.find((r) => ctEach <= r.maxCtEach);
    return row ? row.pricePerCt : null;
  };

  const meleeTotalCarats =
    caratsEach != null && stoneTotal != null && Number.isFinite(stoneTotal)
      ? caratsEach * stoneTotal
      : 0;

  const meleeNaturalPricePerCt = naturalPricePerCtFor(
    NATURAL_PRICE_PER_CARAT_TABLE_MELEE,
    caratsEach,
  );
  const solitaireNaturalPricePerCt = naturalPricePerCtFor(
    NATURAL_PRICE_PER_CARAT_TABLE_SOLITAIRE,
    solitaireCarats,
  );

  const meleeDiamondCost =
    meleeNaturalPricePerCt != null
      ? meleeTotalCarats * meleeNaturalPricePerCt
      : 0;
  const solitaireDiamondCost =
    solitaireCarats != null &&
    Number.isFinite(solitaireCarats) &&
    solitaireNaturalPricePerCt != null
      ? solitaireCarats * solitaireNaturalPricePerCt
      : 0;

  const isLab = /lab/i.test(String(selStoneType || ""));
  const meleeStoneCost =
    meleeDiamondCost * (isLab ? LAB_DIAMOND_MULTIPLIER : 1);
  const solitaireStoneCost =
    solitaireDiamondCost * (isLab ? LAB_DIAMOND_MULTIPLIER : 1);
  const diamondCost =
    (meleeDiamondCost + solitaireDiamondCost) *
    (isLab ? LAB_DIAMOND_MULTIPLIER : 1);

  const baseTotalCost =
    castingCost + polishingCost + stoneSettingCost + diamondCost;
  // Insurance is based on the marked-up sell price (pre-.99 rounding).
  // Solve circular dependency:
  //   insurance = r * sellPriceRaw
  //   sellPriceRaw = m * (baseTotalCost + insurance)
  // => sellPriceRaw = m*baseTotalCost / (1 - m*r)
  const insuranceRate = INSURANCE_COST_PER_RING_PRICE;
  const sellPriceRawUnrounded =
    Number.isFinite(baseTotalCost) && 1 - MARKUP_MULTIPLIER * insuranceRate > 0
      ? (MARKUP_MULTIPLIER * baseTotalCost) /
        (1 - MARKUP_MULTIPLIER * insuranceRate)
      : 0;
  const insuranceCost = sellPriceRawUnrounded * insuranceRate;
  const totalCost = baseTotalCost + insuranceCost;
  const sellPriceRaw = sellPriceRawUnrounded;
  const sellPrice = Number.isFinite(sellPriceRaw)
    ? Math.ceil(sellPriceRaw) - 0.01
    : 0;

  const finalPriceRaw = sellPrice + insuranceCost;
  const finalPrice = Number.isFinite(finalPriceRaw)
    ? Math.ceil(finalPriceRaw) - 0.01
    : 0;

  const computedKey = `${String(selMetal || "")}__${String(selKarat || "")}`;
  const apiComputed = product?.computedPricing?.[computedKey] || null;
  const apiStoneTypes = apiComputed?.stoneTypes || null;
  const apiPricing = /lab/i.test(String(selStoneType || ""))
    ? apiStoneTypes?.lab
    : apiStoneTypes?.natural;
  const apiBreakdown = apiPricing?.breakdown || null;
  const apiInputs = apiPricing?.inputs || null;

  const effectiveInsuranceCost =
    apiBreakdown && typeof apiBreakdown.insuranceCost === "number"
      ? apiBreakdown.insuranceCost
      : insuranceCost;
  const effectiveTotalCost =
    apiBreakdown && typeof apiBreakdown.totalCost === "number"
      ? apiBreakdown.totalCost
      : totalCost;
  const effectiveSellPrice =
    apiBreakdown && typeof apiBreakdown.sellPrice === "number"
      ? apiBreakdown.sellPrice
      : sellPrice;
  const effectiveFinalPrice =
    apiBreakdown && typeof apiBreakdown.finalPrice === "number"
      ? apiBreakdown.finalPrice
      : finalPrice;

  const handleAdd = () => {
    const cartUnitPrice =
      Number.isFinite(effectiveFinalPrice) && effectiveFinalPrice > 0
        ? effectiveFinalPrice
        : unitPrice;
    try {
      const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";
      if (GA4_ID) {
        trackAddToCart({
          measurementId: GA4_ID,
          sku: product.sku,
          name: product.title,
          price: cartUnitPrice,
          quantity: Math.max(1, Number(qty) || 1),
        });
      }
    } catch {}
    const lineItem = {
      id: product._id,
      name: product.title,
      image: primaryImage,
      size: selSize,
      metal: selMetal
        ? selKarat
          ? `${selKarat} ${selMetal}`
          : selMetal
        : null,
      stoneType: selStoneType,
      insuranceCost: effectiveInsuranceCost,
      unitPrice: cartUnitPrice,
      qty: Math.max(1, Number(qty) || 1),
    };
    addToCart(lineItem);
    nav("/cart");
  };

  return (
    <section className="jh-container jh-section">
      <div className="jh-product">
        <div className="jh-detail-media">
          <img
            className="jh-hero"
            src={primaryImage}
            alt={product.title}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            onError={(e) => {
              e.currentTarget.src = placeholderImg;
            }}
          />
          {(currentImages.length > 1 || canShow3DOption) && (
            <div className="jh-thumbs">
              {currentImages.map((im, i) => (
                <img
                  key={i}
                  className={`jh-thumb ${i === active ? "jh-thumb--active" : ""}`}
                  src={im.src || placeholderImg}
                  alt={im.alt || `${product.title} ${i + 1}`}
                  decoding="async"
                  onClick={() => setActive(i)}
                  onError={(e) => {
                    e.currentTarget.src = placeholderImg;
                  }}
                />
              ))}
              {canShow3DOption ? (
                <button
                  type="button"
                  className="jh-thumb"
                  onClick={open3D}
                  aria-label="View in 3D"
                  style={{
                    display: "grid",
                    placeItems: "center",
                    background: "#0b0b0c",
                    color: "#fff",
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    border: "1px solid rgba(255,255,255,0.25)",
                    cursor: "pointer",
                  }}
                >
                  3D
                </button>
              ) : null}
            </div>
          )}
        </div>

        <div className="jh-detail-body">
          <button className="jh-back" onClick={() => nav(-1)}>
            ← Back
          </button>
          <h2 className="jh-detail-title">{product.title}</h2>
          {product.brand && (
            <div className="jh-rating" style={{ marginBottom: 6 }}>
              by {product.brand}
            </div>
          )}
          <div className="jh-price">
            {effectiveFinalPrice ? (
              currency(effectiveFinalPrice)
            ) : product.salePrice ? (
              <>
                <s>{currency(product.price)}</s>
                <span>{currency(product.salePrice)}</span>
              </>
            ) : (
              <span>{currency(unitPrice)}</span>
            )}
          </div>
          {SHOW_COST_FIELDS ? (
            <CostFields
              goldErr={goldErr}
              usingComputedPricing={Boolean(apiPricing && apiBreakdown)}
              metalCost={
                apiBreakdown &&
                typeof apiBreakdown.castingMetalCost === "number"
                  ? apiBreakdown.castingMetalCost
                  : metalCost
              }
              isPlatinum={isPlatinum}
              selKarat={selKarat}
              metalWeightGrams={
                apiInputs && typeof apiInputs.metalWeightGrams === "number"
                  ? apiInputs.metalWeightGrams
                  : metalWeightGrams
              }
              castingDwt={
                apiBreakdown && typeof apiBreakdown.castingDwt === "number"
                  ? apiBreakdown.castingDwt
                  : castingDwt
              }
              castingCost={
                apiBreakdown && typeof apiBreakdown.castingCost === "number"
                  ? apiBreakdown.castingCost
                  : castingCost
              }
              meleeStoneCost={
                apiBreakdown && typeof apiBreakdown.meleeStoneCost === "number"
                  ? apiBreakdown.meleeStoneCost
                  : meleeStoneCost
              }
              solitaireStoneCost={
                apiBreakdown &&
                typeof apiBreakdown.solitaireStoneCost === "number"
                  ? apiBreakdown.solitaireStoneCost
                  : solitaireStoneCost
              }
              laborCost={
                apiBreakdown && typeof apiBreakdown.laborCost === "number"
                  ? apiBreakdown.laborCost
                  : laborCost
              }
              insuranceCost={effectiveInsuranceCost}
              totalCost={effectiveTotalCost}
              sellPrice={effectiveSellPrice}
              finalPrice={effectiveFinalPrice}
            />
          ) : null}
          {product.description && (
            <p className="jh-desc" style={{ whiteSpace: "pre-line" }}>
              {String(product.description).replace(
                /%metal_type%/g,
                String(selMetal || product.defaultOptions?.metal_type || ""),
              )}
            </p>
          )}

          {product.metals?.length ? (
            <div className="jh-field">
              <label className="jh-label">Metal</label>
              <div className="jh-sizes">
                {product.metals.map((m) => (
                  <button
                    type="button"
                    key={m}
                    className={`jh-chip ${m === selMetal ? "jh-chip--active" : ""}`}
                    onClick={() => setSelMetal(m)}
                    aria-pressed={m === selMetal}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {isPlatinum ? (
            <div className="jh-field">
              <label className="jh-label">Platinum Purity</label>
              <div className="jh-sizes">
                {platinumPurities.map((k) => (
                  <button
                    type="button"
                    key={k}
                    className={`jh-chip ${k === selKarat ? "jh-chip--active" : ""}`}
                    onClick={() => setSelKarat(k)}
                    aria-pressed={k === selKarat}
                  >
                    {k}
                  </button>
                ))}
              </div>
            </div>
          ) : product.karats?.length ? (
            <div className="jh-field">
              <label className="jh-label">Metal Karat</label>
              <div className="jh-sizes">
                {product.karats.map((k) => (
                  <button
                    type="button"
                    key={k}
                    className={`jh-chip ${k === selKarat ? "jh-chip--active" : ""}`}
                    onClick={() => setSelKarat(k)}
                    aria-pressed={k === selKarat}
                  >
                    {k}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {product.sizes?.length ? (
            <div className="jh-field">
              <label className="jh-label">Ring size</label>
              <div className="jh-sizes">
                {product.sizes.map((s) => (
                  <button
                    type="button"
                    key={s}
                    className={`jh-chip ${String(s) === String(selSize) ? "jh-chip--active" : ""}`}
                    onClick={() => setSelSize(s)}
                    aria-pressed={String(s) === String(selSize)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="jh-field">
            <label className="jh-label">Stone Type</label>
            <div className="jh-sizes">
              {["Natural diamond", "Lab diamond"].map((s) => (
                <button
                  type="button"
                  key={s}
                  className={`jh-chip ${s === selStoneType ? "jh-chip--active" : ""}`}
                  onClick={() => setSelStoneType(s)}
                  aria-pressed={s === selStoneType}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {product.tags?.length ? (
            <div className="jh-tags">
              {product.tags.map((t) => (
                <span key={t} className="jh-tag">
                  {t}
                </span>
              ))}
            </div>
          ) : null}

          {product.sizes?.length ? (
            <div className="jh-field">
              <label className="jh-label" htmlFor="qty">
                Quantity
              </label>
              <input
                id="qty"
                className="jh-input"
                type="number"
                min={1}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
              />
            </div>
          ) : null}

          <div className="jh-actions">
            <button
              className="jh-btn"
              onClick={handleAdd}
              disabled={unitPrice == null && !effectiveFinalPrice}
            >
              Add to cart
            </button>
            <Link to="/cart" className="jh-btn jh-btn--ghost">
              View cart
            </Link>
          </div>
        </div>
      </div>

      {show3D ? (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setShow3D(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setShow3D(false);
          }}
          tabIndex={-1}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(980px, 100%)",
              height: "min(720px, 80vh)",
              background: "#ffffff",
              borderRadius: 14,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <button
              type="button"
              className="jh-btn"
              onClick={() => setShow3D(false)}
              style={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}
            >
              Close
            </button>

            {threeDCheckStatus === "checking" ? (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  placeItems: "center",
                  background: "#ffffff",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 12,
                    padding: 18,
                    borderRadius: 12,
                    background: "rgba(255,255,255,0.9)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                    fontWeight: 600,
                    color: "#111827",
                  }}
                >
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: "999px",
                      border: "4px solid rgba(17,24,39,0.15)",
                      borderTopColor: "rgba(17,24,39,0.8)",
                      animation: "jhSpin 0.9s linear infinite",
                    }}
                  />
                  Loading 3D...
                </div>
              </div>
            ) : threeDCheckStatus === "error" ? (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  placeItems: "center",
                  background: "#ffffff",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 12,
                    padding: 18,
                    borderRadius: 12,
                    background: "rgba(255,255,255,0.95)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                    fontWeight: 600,
                    color: "#111827",
                    maxWidth: 420,
                    textAlign: "center",
                  }}
                >
                  3D model not available.
                  <div style={{ fontWeight: 400, fontSize: 12 }}>
                    {threeDCheckError}
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    {/* <button type="button" className="jh-btn" onClick={open3D}>
                      Retry
                    </button> */}
                    <button
                      type="button"
                      className="jh-btn jh-btn--ghost"
                      onClick={() => setShow3D(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <Canvas
                shadows
                dpr={[1, 2]}
                gl={{ antialias: true, physicallyCorrectLights: true }}
                onCreated={({ gl }) => {
                  gl.outputColorSpace = THREE.SRGBColorSpace;
                  gl.toneMapping = THREE.ACESFilmicToneMapping;
                  gl.toneMappingExposure = 1.35;
                  gl.shadowMap.enabled = true;
                  gl.shadowMap.type = THREE.PCFSoftShadowMap;
                }}
                camera={{ position: [0, 0, 8], fov: 45 }}
              >
                <color attach="background" args={["#ffffff"]} />
                <ambientLight intensity={0.12} />
                <directionalLight
                  castShadow
                  intensity={2.2}
                  position={[3, 4, 4]}
                  shadow-mapSize={[2048, 2048]}
                  shadow-camera-near={0.1}
                  shadow-camera-far={20}
                  shadow-camera-left={-5}
                  shadow-camera-right={5}
                  shadow-camera-top={5}
                  shadow-camera-bottom={-5}
                />
                <directionalLight intensity={0.9} position={[-4, 2, 3]} />
                <directionalLight intensity={1.1} position={[-2, 3, -4]} />

                <mesh
                  rotation-x={-Math.PI / 2}
                  position={[0, -1, 0]}
                  receiveShadow
                >
                  <planeGeometry args={[20, 20]} />
                  <shadowMaterial transparent opacity={0.22} />
                </mesh>

                <ThreeDErrorBoundary
                  fallback={(err) => (
                    <group>
                      <Html center>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 12,
                            padding: 18,
                            borderRadius: 12,
                            background: "rgba(255,255,255,0.95)",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                            fontWeight: 600,
                            color: "#111827",
                            maxWidth: 420,
                            textAlign: "center",
                          }}
                        >
                          Could not load 3D model.
                          <div style={{ fontWeight: 400, fontSize: 12 }}>
                            {String(err?.message || err || "")}
                          </div>
                          <div style={{ display: "flex", gap: 10 }}>
                            {/* <button
                              type="button"
                              className="jh-btn"
                              onClick={() => setThreeDRetryKey((k) => k + 1)}
                            >
                              Retry
                            </button> */}
                            <button
                              type="button"
                              className="jh-btn jh-btn--ghost"
                              onClick={() => setShow3D(false)}
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </Html>
                    </group>
                  )}
                >
                  <Suspense
                    fallback={
                      <group>
                        <Html center>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 12,
                              padding: 18,
                              borderRadius: 12,
                              background: "rgba(255,255,255,0.9)",
                              boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                              fontWeight: 600,
                              color: "#111827",
                            }}
                          >
                            <div
                              style={{
                                width: 38,
                                height: 38,
                                borderRadius: "999px",
                                border: "4px solid rgba(17,24,39,0.15)",
                                borderTopColor: "rgba(17,24,39,0.8)",
                                animation: "jhSpin 0.9s linear infinite",
                              }}
                            />
                            Loading 3D...
                          </div>
                        </Html>
                      </group>
                    }
                  >
                    <RingScene
                      key={`${modelUrl}:${threeDRetryKey}`}
                      modelUrl={modelUrl}
                      metalType={selMetal}
                      enableScroll={false}
                    />
                  </Suspense>
                </ThreeDErrorBoundary>
              </Canvas>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default ProductDetailPage;
